import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { apiError } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { hasSpecialPermission, hasModule } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Trash, MagnifyingGlass, AddressBook, WarningCircle, ClockCounterClockwise } from "@phosphor-icons/react";

const inputCls = "bg-zinc-950 border-zinc-800 rounded-sm text-zinc-100 focus:border-emerald-600 focus-visible:ring-emerald-600";
const lbl = "text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block";

const blank = () => ({ name: "", contact_person: "", email: "", mobile: "", city: "", state: "", gst: "", billing_address: "", code: "", notes: "" });

export default function Customers() {
  const { user } = useAuth();
  const canAdd = user.role === "admin" || hasSpecialPermission(user, "customer_add");
  const canEditMaster = user.role === "admin" || hasSpecialPermission(user, "customer_edit");
  const canDelete = user.role === "admin" || user.role === "manager";
  const canViewOrders = hasModule(user, "orders");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [detailsForm, setDetailsForm] = useState(blank());
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState("all");

  const load = (search = "") => {
    setLoading(true);
    api.get("/customers", { params: { search, limit: 500 } }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(() => load(q), 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live duplicate-name check while adding a new customer.
  useEffect(() => {
    if (!open) { setDupWarning(null); return; }
    const name = form.name.trim();
    if (!name) { setDupWarning(null); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get("/customers", { params: { search: name, limit: 5 } });
      const match = data.find((c) => c.name.toLowerCase() === name.toLowerCase());
      setDupWarning(match ? `A customer named "${match.name}" already exists.` : null);
    }, 350);
    return () => clearTimeout(t);
  }, [form.name, open]);

  const openCreate = () => { setForm(blank()); setDupWarning(null); setOpen(true); };
  const openDetails = (c) => { setDetailsTarget(c); setDetailsForm({ ...blank(), ...c }); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Company / Firm name is required");
    setSaving(true);
    try {
      await api.post("/customers", form);
      toast.success("Customer added");
      setOpen(false);
      load(q);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not save customer"); }
    finally { setSaving(false); }
  };

  const saveDetails = async () => {
    setDetailsSaving(true);
    try {
      const { data } = await api.put(`/customers/${detailsTarget.id}`, { gst: detailsForm.gst, billing_address: detailsForm.billing_address, city: detailsForm.city, state: detailsForm.state, notes: detailsForm.notes });
      toast.success("Customer updated");
      setDetailsTarget(data);
      setDetailsForm({ ...blank(), ...data });
      load(q);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not save customer"); }
    finally { setDetailsSaving(false); }
  };


  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/customers/${deleteTarget.id}`);
      toast.success("Customer deleted");
      setSelected((s) => { const n = new Set(s); n.delete(deleteTarget.id); return n; });
      load(q);
    }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not delete customer"); }
    finally { setDeleteTarget(null); }
  };

  const toggleAll = () => setSelected(items.length && selected.size === items.length ? new Set() : new Set(items.map((c) => c.id)));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected customer(s)? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/customers/bulk-delete", { ids: [...selected] });
      toast.success(`Deleted ${data.deleted} customer(s)`);
      setSelected(new Set());
      load(q);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not delete selected customers"); }
    finally { setBulkDeleting(false); }
  };

  const openHistory = async (c) => {
    setHistoryTarget(c);
    setHistoryFilter("all");
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/customers/${c.id}/orders`);
      setHistoryOrders(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not load order history"); }
    finally { setHistoryLoading(false); }
  };

  const filteredHistory = historyFilter === "all" ? historyOrders : historyOrders.filter((o) => o.status === historyFilter);

  return (
    <div className="max-w-6xl" data-testid="customers-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2"><AddressBook size={24} className="text-emerald-600" /> Customer Master</h1>
          <p className="text-sm text-zinc-500 mt-1">Company details fetched into every order's Account Section.</p>
        </div>
        {canAdd && (
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm gap-1" data-testid="add-customer-btn">
            <Plus weight="bold" size={16} /> Add Customer
          </Button>
        )}
      </div>

      <div className="relative w-full max-w-xs mb-4">
        <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or contact person…" className={`${inputCls} pl-9`} data-testid="customers-search" />
      </div>

      {canDelete && selected.size > 0 && (
        <div className="flex items-center justify-between mb-3 border border-emerald-600/40 bg-emerald-600/5 px-4 py-2.5 rounded-sm" data-testid="customers-bulk-bar">
          <span className="text-sm text-zinc-200">{selected.size} customer(s) selected</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setSelected(new Set())} className="border-zinc-700 bg-zinc-950 text-zinc-300 rounded-sm text-xs">Clear</Button>
            <Button size="sm" onClick={bulkDelete} disabled={bulkDeleting} data-testid="customers-bulk-delete-btn" className="bg-red-600 hover:bg-red-700 text-white rounded-sm text-xs gap-1"><Trash size={14} /> {bulkDeleting ? "Deleting…" : "Delete selected"}</Button>
          </div>
        </div>
      )}

      <div className="border border-zinc-800 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-zinc-500 text-xs uppercase tracking-wider">
            <tr>
              {canDelete && <th className="px-4 py-2.5 w-10"><Checkbox checked={items.length > 0 && selected.size === items.length} onCheckedChange={toggleAll} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid="customers-select-all" /></th>}
              <th className="text-left px-4 py-2.5">Company / Firm Name</th>
              <th className="text-left px-4 py-2.5">Contact Person</th>
              <th className="text-left px-4 py-2.5">Mobile No</th>
              <th className="text-left px-4 py-2.5">City / State</th>
              <th className="text-left px-4 py-2.5">GST No</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} onDoubleClick={() => openDetails(c)} title="Double-click for full details" className={`border-t border-zinc-800 hover:bg-zinc-900/30 cursor-pointer ${selected.has(c.id) ? "bg-emerald-600/5" : ""}`} data-testid={`customer-row-${c.id}`}>
                {canDelete && (
                  <td className="px-4 py-3">
                    <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600" data-testid={`customers-select-${c.id}`} />
                  </td>
                )}
                <td className="px-4 py-3 text-zinc-100 font-medium">{c.name}</td>
                <td className="px-4 py-3 text-zinc-400">{c.contact_person || "—"}</td>
                <td className="px-4 py-3 text-zinc-400">{c.mobile || "—"}</td>
                <td className="px-4 py-3 text-zinc-400">{[c.city, c.state].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-4 py-3 text-zinc-400">{c.gst || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-3">
                    {canViewOrders && <button onClick={() => openHistory(c)} title="Order history" className="text-zinc-500 hover:text-emerald-400 transition-colors" data-testid={`history-customer-${c.id}`}><ClockCounterClockwise size={15} /></button>}
                    {canDelete && <button onClick={() => setDeleteTarget(c)} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid={`delete-customer-${c.id}`}><Trash size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={canDelete ? 7 : 6} className="px-4 py-8 text-center text-zinc-500">{q ? "No customers match your search." : "No customers yet. Add one, or they're created automatically from New Order."}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle>Add Customer</DialogTitle>
            <DialogDescription className="text-zinc-500">Enter company details for the Customer Master.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
            <div>
              <label className={lbl}>Company / Firm Name *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="customer-form-name" />
              {dupWarning && (
                <p className="text-[11px] text-amber-400 mt-1 flex items-center gap-1" data-testid="customer-name-dup-warning"><WarningCircle size={12} /> {dupWarning}</p>
              )}
            </div>
            <div>
              <label className={lbl}>Contact Person</label>
              <Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className={inputCls} data-testid="customer-form-contact" />
            </div>
            <div>
              <label className={lbl}>Email</label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="customer-form-email" />
            </div>
            <div>
              <label className={lbl}>Mobile No</label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={inputCls} data-testid="customer-form-mobile" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>City</label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="customer-form-city" />
              </div>
              <div>
                <label className={lbl}>State</label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls} data-testid="customer-form-state" />
              </div>
            </div>
            <div>
              <label className={lbl}>GST No</label>
              <Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} className={inputCls} data-testid="customer-form-gst" />
            </div>
            <div>
              <label className={lbl}>Address</label>
              <Textarea value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} className={`${inputCls} min-h-[70px]`} data-testid="customer-form-billing" />
            </div>
            <div className="pt-2 border-t border-zinc-800">
              <label className={lbl}>Customer Code <span className="text-zinc-600">(internal, optional)</span></label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className={inputCls} data-testid="customer-form-code" />
            </div>
            <div>
              <label className={lbl}>Notes <span className="text-zinc-600">(internal, optional)</span></label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} min-h-[50px]`} data-testid="customer-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-zinc-700 rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="customer-form-save">
              {saving ? "Saving…" : "Add Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailsTarget} onOpenChange={(v) => !v && setDetailsTarget(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg" data-testid="customer-details-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AddressBook size={18} className="text-emerald-600" /> {detailsTarget?.name}</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {canEditMaster ? "Full Customer Master record — GST, address, city, state and notes can be edited directly here." : "Full Customer Master record."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5 max-h-[65vh] overflow-y-auto pr-1" data-testid="customer-details-body">
            {[
              ["Company / Firm Name", detailsTarget?.name],
              ["Contact Person", detailsTarget?.contact_person],
              ["Email", detailsTarget?.email],
              ["Mobile No", detailsTarget?.mobile],
              ["Customer Code", detailsTarget?.code],
            ].map(([lbl2, val]) => (
              <div key={lbl2} className="grid grid-cols-3 gap-3 text-sm border-b border-zinc-900 pb-2">
                <div className="text-zinc-500 uppercase text-[11px] font-bold tracking-wider pt-0.5">{lbl2}</div>
                <div className="col-span-2 text-zinc-100 break-words">{val || "—"}</div>
              </div>
            ))}
            {canEditMaster ? (
              <>
                <div className="grid grid-cols-2 gap-3 pt-1">
                  <div>
                    <label className={lbl}>City</label>
                    <Input value={detailsForm.city} onChange={(e) => setDetailsForm({ ...detailsForm, city: e.target.value })} className={inputCls} data-testid="details-form-city" />
                  </div>
                  <div>
                    <label className={lbl}>State</label>
                    <Input value={detailsForm.state} onChange={(e) => setDetailsForm({ ...detailsForm, state: e.target.value })} className={inputCls} data-testid="details-form-state" />
                  </div>
                </div>
                <div>
                  <label className={lbl}>GST No</label>
                  <Input value={detailsForm.gst} onChange={(e) => setDetailsForm({ ...detailsForm, gst: e.target.value })} className={inputCls} data-testid="details-form-gst" />
                </div>
                <div>
                  <label className={lbl}>Billing Address</label>
                  <Textarea value={detailsForm.billing_address} onChange={(e) => setDetailsForm({ ...detailsForm, billing_address: e.target.value })} className={`${inputCls} min-h-[70px]`} data-testid="details-form-billing" />
                </div>
                <div>
                  <label className={lbl}>Notes <span className="text-zinc-600">(internal, optional)</span></label>
                  <Textarea value={detailsForm.notes} onChange={(e) => setDetailsForm({ ...detailsForm, notes: e.target.value })} className={`${inputCls} min-h-[50px]`} data-testid="details-form-notes" />
                </div>
              </>
            ) : (
              [
                ["City", detailsTarget?.city], ["State", detailsTarget?.state], ["GST No", detailsTarget?.gst],
                ["Billing Address", detailsTarget?.billing_address], ["Notes", detailsTarget?.notes],
              ].map(([lbl2, val]) => (
                <div key={lbl2} className="grid grid-cols-3 gap-3 text-sm border-b border-zinc-900 pb-2">
                  <div className="text-zinc-500 uppercase text-[11px] font-bold tracking-wider pt-0.5">{lbl2}</div>
                  <div className="col-span-2 text-zinc-100 break-words">{val || "—"}</div>
                </div>
              ))
            )}
          </div>
          <DialogFooter className="flex-wrap gap-2">
            {canViewOrders && (
              <Button variant="outline" onClick={() => { openHistory(detailsTarget); setDetailsTarget(null); }} className="border-zinc-700 rounded-sm gap-1.5" data-testid="details-view-history"><ClockCounterClockwise size={14} /> Order History</Button>
            )}
            <Button variant="outline" onClick={() => setDetailsTarget(null)} className="border-zinc-700 rounded-sm" data-testid="details-close">Close</Button>
            {canEditMaster && (
              <Button onClick={saveDetails} disabled={detailsSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm" data-testid="details-save">
                {detailsSaving ? "Saving…" : "Save Changes"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyTarget} onOpenChange={(v) => !v && setHistoryTarget(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl" data-testid="customer-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClockCounterClockwise size={18} className="text-emerald-600" /> Order History — {historyTarget?.name}</DialogTitle>
            <DialogDescription className="text-zinc-500">All current and past orders placed by this customer.</DialogDescription>
          </DialogHeader>
          {historyOrders.length > 0 && (
            <div className="flex items-center gap-2" data-testid="customer-history-filters">
              {["all", "active", "completed"].map((f) => (
                <button
                  key={f} onClick={() => setHistoryFilter(f)}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${historyFilter === f ? "bg-emerald-600 text-white border-emerald-600 font-semibold" : "border-zinc-700 text-zinc-400 hover:bg-zinc-800"}`}
                  data-testid={`customer-history-filter-${f}`}
                >
                  {f === "all" ? "All" : f === "active" ? "Active" : "Completed"}
                </button>
              ))}
            </div>
          )}
          <div className="max-h-[60vh] overflow-y-auto">
            {historyLoading ? (
              <div className="text-sm text-zinc-500 py-8 text-center">Loading…</div>
            ) : filteredHistory.length === 0 ? (
              <div className="text-sm text-zinc-500 py-8 text-center" data-testid="customer-history-empty">
                {historyOrders.length === 0 ? "No orders found for this customer yet." : `No ${historyFilter} orders for this customer.`}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-zinc-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-2 py-2">Order No</th>
                    <th className="text-left px-2 py-2">Name</th>
                    <th className="text-left px-2 py-2">Status</th>
                    <th className="text-left px-2 py-2">Order Date</th>
                    <th className="text-right px-2 py-2">Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.map((o) => (
                    <tr key={o.id} className="border-t border-zinc-800 hover:bg-zinc-900/30" data-testid={`customer-history-row-${o.id}`}>
                      <td className="px-2 py-2.5">
                        <Link to={`/orders/${o.id}`} className="text-emerald-400 hover:underline" onClick={() => setHistoryTarget(null)} data-testid={`customer-history-link-${o.id}`}>{o.order_no}</Link>
                      </td>
                      <td className="px-2 py-2.5 text-zinc-300">{o.name}</td>
                      <td className="px-2 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${o.status === "completed" ? "bg-emerald-500/10 text-emerald-400" : "bg-emerald-600/10 text-emerald-400"}`}>{o.status}</span>
                      </td>
                      <td className="px-2 py-2.5 text-zinc-400">{o.order_date || "—"}</td>
                      <td className="px-2 py-2.5 text-right text-zinc-400">{o.progress ?? 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryTarget(null)} className="border-zinc-700 rounded-sm" data-testid="customer-history-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="delete-customer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete customer?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently delete <span className="text-zinc-200 font-semibold">"{deleteTarget?.name}"</span> from Customer Master. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-900 border-zinc-700 text-zinc-200 hover:bg-zinc-800 rounded-sm" data-testid="delete-customer-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="delete-customer-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
